import { useEffect, useState } from "react";
import { useApp } from "@/context/AppContext";
import api from "@/lib/api";
import { formatMoney, formatDate } from "@/lib/format";
import { Repeat, CalendarClock, AlertCircle, Clock, Loader2, ArrowDownLeft, ArrowUpRight } from "lucide-react";
import { toast } from "sonner";

const STATUS = {
  overdue: { label: "Overdue", cls: "bg-rose-50 text-rose-600", icon: AlertCircle },
  due_soon: { label: "Due soon", cls: "bg-amber-50 text-amber-600", icon: Clock },
  upcoming: { label: "Upcoming", cls: "bg-emerald-50 text-emerald-600", icon: CalendarClock },
};

export default function Recurring() {
  const { companyId, currency } = useApp();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!companyId) return;
    setLoading(true);
    api.get(`/companies/${companyId}/recurring`).then(({ data }) => setItems(data)).catch(() => toast.error("Failed to load recurring payments")).finally(() => setLoading(false));
  }, [companyId]);

  const reminders = items.filter((i) => i.status !== "upcoming");
  const dueLabel = (i) => (i.days_to < 0 ? `${Math.abs(i.days_to)}d overdue` : i.days_to === 0 ? "Due today" : `in ${i.days_to}d`);

  if (loading)
    return <div className="grid place-items-center h-96"><Loader2 className="animate-spin text-indigo-600" size={32} /></div>;

  return (
    <div className="space-y-6 animate-fade-up" data-testid="recurring-page">
      <div>
        <h1 className="text-2xl md:text-3xl font-heading font-extrabold text-slate-900">Recurring Payments</h1>
        <p className="text-slate-500 text-sm mt-0.5">{items.length} detected recurring items with predicted next-due dates</p>
      </div>

      {/* Reminder banner */}
      {reminders.length > 0 && (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="recurring-reminders">
          {reminders.slice(0, 6).map((i, idx) => {
            const s = STATUS[i.status];
            return (
              <div key={idx} className={`rounded-2xl border p-4 ${i.status === "overdue" ? "border-rose-200 bg-rose-50/40" : "border-amber-200 bg-amber-50/40"}`}>
                <div className="flex items-center gap-2">
                  <s.icon size={16} className={i.status === "overdue" ? "text-rose-600" : "text-amber-600"} />
                  <span className={`text-xs font-bold uppercase tracking-wide ${i.status === "overdue" ? "text-rose-600" : "text-amber-600"}`}>{s.label} · {dueLabel(i)}</span>
                </div>
                <p className="font-heading font-bold text-slate-900 mt-2 truncate">{i.description}</p>
                <p className="text-sm text-slate-500">{formatMoney(i.avg_amount, currency)} · {i.frequency} · next {formatDate(i.next_due)}</p>
              </div>
            );
          })}
        </div>
      )}

      {/* Table */}
      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[760px]">
            <thead>
              <tr className="border-b border-slate-100 text-xs uppercase tracking-wider text-slate-500">
                <th className="text-left px-5 py-3 font-semibold">Payment</th>
                <th className="text-left px-5 py-3 font-semibold">Frequency</th>
                <th className="text-left px-5 py-3 font-semibold hidden md:table-cell">Last Paid</th>
                <th className="text-left px-5 py-3 font-semibold">Next Due</th>
                <th className="text-right px-5 py-3 font-semibold">Avg Amount</th>
                <th className="text-right px-5 py-3 font-semibold">Status</th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 ? (
                <tr><td colSpan={6} className="text-center py-14 text-slate-400">
                  No recurring payments detected yet. They appear once a description repeats 3+ times.
                </td></tr>
              ) : items.map((i, idx) => {
                const s = STATUS[i.status];
                return (
                  <tr key={idx} data-testid={`recurring-row-${idx}`} className="border-b border-slate-50 hover:bg-slate-50">
                    <td className="px-5 py-3">
                      <div className="flex items-center gap-2">
                        <span className={`w-7 h-7 rounded-lg grid place-items-center shrink-0 ${i.type === "credit" ? "bg-emerald-50 text-emerald-600" : "bg-indigo-50 text-indigo-600"}`}>
                          {i.type === "credit" ? <ArrowDownLeft size={15} /> : <Repeat size={15} />}
                        </span>
                        <div>
                          <p className="font-semibold text-slate-800">{i.description}</p>
                          <p className="text-xs text-slate-400">{i.category} · {i.count} payments</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-5 py-3"><span className="text-xs font-semibold px-2 py-1 rounded-lg bg-slate-100 text-slate-600">{i.frequency}</span></td>
                    <td className="px-5 py-3 text-slate-500 hidden md:table-cell">{formatDate(i.last_date)}</td>
                    <td className="px-5 py-3 font-semibold text-slate-800">{formatDate(i.next_due)}</td>
                    <td className={`px-5 py-3 text-right font-bold ${i.type === "credit" ? "text-emerald-600" : "text-slate-800"}`}>{formatMoney(i.avg_amount, currency)}</td>
                    <td className="px-5 py-3 text-right">
                      <span className={`inline-flex items-center gap-1 text-xs font-bold px-2.5 py-1 rounded-lg ${s.cls}`}>
                        <s.icon size={12} /> {dueLabel(i)}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
